import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import styles from './styles.module.scss'
import Button from '../../components/Button'
import { IoIosAdd } from 'react-icons/io'
import { BiTrash } from 'react-icons/bi'
import Modal from './Modal'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import { deleteDish, getDish, getDishList, getGroupNutritionList, getPeriodNutritionList } from '../../actions/directoryCanteen'
import { GET_DISH_FILE } from '../../actions/types'


export default function Dishes() {
  const dispatch = useDispatch()
  const { periodNutritionList, dish, loading } = useSelector(state => state.directoryCanteen)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortField, setSortField] = useState('name')
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getPeriodNutritionList())
    dispatch(getGroupNutritionList())
    dispatch(getDishList())
    dispatch(getDish())
  }, [])

  const openModal = useCallback((data) => () => {
    setSelectedItem(data || {})
  }, [])

  const closeModal = useCallback(() => {
    dispatch({ type: GET_DISH_FILE, payload: null })
    setSelectedItem(null)
  }, [dispatch])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.xp_key, name: item.name })
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteDish(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }, [sortField])

  const sortedList = useMemo(() => {
    if (!dish) return []
    return [...dish].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortAsc ? aVal - bVal : bVal - aVal
      return sortAsc
        ? String(aVal).localeCompare(String(bVal), 'ru')
        : String(bVal).localeCompare(String(aVal), 'ru')
    })
  }, [dish, sortField, sortAsc])

  const renderBody = () => {
    if (!sortedList.length) {
      return (
        <tr>
          <td className={styles.emptyRow} colSpan={6 + (periodNutritionList?.length || 0)}>
            Нет добавленных блюд
          </td>
        </tr>
      )
    }

    return sortedList.map((data) => {
      const { name, kcal, exit, edizm, xp_key, dishclass_name, has_photo, eat_period } = data
      return (
        <tr key={xp_key} className={styles.row} onClick={openModal(data)}>
          <td className={styles.cellName}>
            <span title={name}>{name}</span>
          </td>
          <td className={styles.cellNumeric}>{exit}</td>
          <td className={styles.cellCenter}>{edizm}</td>
          <td className={styles.cellNumeric}>{kcal}</td>
          <td className={styles.cellCenter}>
            {has_photo === 1 ? <span className={styles.pill}>Фото</span> : '—'}
          </td>
          <td className={styles.cellText}>{dishclass_name}</td>
          {periodNutritionList?.map((el) => (
            <td key={el.xp_key} className={styles.cellCenter}>
              {eat_period.find(eat => eat.eat_period_xp_key === el.xp_key) ? '✔' : ''}
            </td>
          ))}
          <td className={styles.cellActions} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={handleDelete(data)}
              title={`Удалить блюдо ${name}`}
              aria-label={`Удалить блюдо ${name}`}
            >
              <BiTrash size={18} />
            </button>
          </td>
        </tr>
      )
    })
  }

  return (
    <div className={styles.wrapper}>
      <h1>Блюда</h1>

      {loading ? (
        <div className={styles.loadingState}>
          <Puff height={50} width={50} color="#3689FF" ariaLabel="Загрузка..." />
          <span>Загрузка блюд...</span>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <Table className={styles.tables} maxHeight={'70vh'}>
            <thead>
              <tr>
                <th className={styles.headerSticky} onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Название</th>
                <th className={styles.headerSticky}>Выход (по умолчанию)</th>
                <th className={styles.headerSticky}>Ед. изм</th>
                <th className={styles.headerSticky} onClick={() => handleSort('kcal')} style={{ cursor: 'pointer' }}>Ккал</th>
                <th className={styles.headerSticky}>Фото</th>
                <th className={styles.headerSticky} onClick={() => handleSort('dishclass_name')} style={{ cursor: 'pointer' }}>Тип блюда</th>
                {periodNutritionList?.map((el) => (
                  <th key={el.xp_key} className={styles.headerSticky}>{el.name}</th>
                ))}
                <th className={styles.headerSticky}></th>
              </tr>
            </thead>
            <tbody>{renderBody()}</tbody>
          </Table>
        </div>
      )}

      <div className={styles.actionsRow}>
        <Button
          style={{ padding: '.5rem 1.5rem' }}
          onClick={openModal()}
        >
          <IoIosAdd size={20} style={{ marginRight: 8 }} />
          Добавить
        </Button>
      </div>

      {selectedItem && (
        <Modal
          closeModal={closeModal}
          data={selectedItem}
          width="830px"
          addMode={!selectedItem?.xp_key}
        />
      )}

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить ${confirmation.name}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}
